import { Routes, Route } from 'react-router-dom';
import { useAttributionCapture } from './hooks/useAttribution';
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { CampaignProvider } from './context/CampaignContext';
import type { ActiveCampaign } from './api/client';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CartToast from './components/CartToast';
import ViewCartBanner from './components/ViewCartBanner';
import HomePage from './pages/HomePage';
import StorePage from './pages/StorePage';
import ProductDetailPage from './pages/ProductDetailPage';
import AboutPage from './pages/AboutPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ScrollToTop from './components/ScrollToTop';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProfilePage from './pages/ProfilePage';
import MyOrdersPage from './pages/MyOrdersPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ContactPage from './pages/ContactPage';
import WholesalePage from './pages/WholesalePage';
import RewardsPage from './pages/RewardsPage';
import MyCoinsPage from './pages/MyCoinsPage';
import FaqPage from './pages/FaqPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogDetailPage from './pages/BlogDetailPage';
import RefundPolicyPage from './pages/policies/RefundPolicyPage';
import ShippingPolicyPage from './pages/policies/ShippingPolicyPage';
import TermsOfServicePage from './pages/policies/TermsOfServicePage';
import PrivacyPolicyPage from './pages/policies/PrivacyPolicyPage';
import { ProductsProvider } from './context/ProductsContext';
import NotFoundPage from './pages/NotFoundPage';
import CityPage from './pages/CityPage';
import CityIndexPage from './pages/CityIndexPage';
import BrandPage from './pages/BrandPage';

// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import InventoryPage from './pages/admin/InventoryPage';
import OrdersPage from './pages/admin/OrdersPage';
import CouponsPage from './pages/admin/CouponsPage';
import WholesaleInquiriesPage from './pages/admin/WholesaleInquiriesPage';
import RewardsAdminPage from './pages/admin/RewardsAdminPage';
import CampaignsPage from './pages/admin/CampaignsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import AIInsightsPage from './pages/admin/AIInsightsPage';
import GoogleAnalyticsPage from './pages/admin/GoogleAnalyticsPage';
import UsersPage from './pages/admin/UsersPage';
import AccountsPage from './pages/admin/AccountsPage';

function AttributionTracker() {
  useAttributionCapture();
  return null;
}

interface AppInitialData {
  // Loose shape on purpose — ProductsProvider casts. The strict type
  // lives next to the server prefetcher (src/ssr/prefetch.ts).
  products?: unknown[];
  activeCampaign?: unknown;
}

interface AppProps {
  initialData?: AppInitialData;
}

function App({ initialData }: AppProps) {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProductsProvider initialProducts={initialData?.products}>
          <CampaignProvider
            initialCampaign={(initialData?.activeCampaign ?? null) as ActiveCampaign | null}
            seeded={initialData?.activeCampaign !== undefined}
          >
          <CartProvider>
            <AttributionTracker />
            <ScrollToTop />
            <Routes>
              {/* Admin routes — no Navbar/Footer */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="coupons" element={<CouponsPage />} />
                <Route path="wholesale" element={<WholesaleInquiriesPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="rewards" element={<RewardsAdminPage />} />
                <Route path="accounts" element={<AccountsPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="google-analytics" element={<GoogleAnalyticsPage />} />
                <Route path="ai-insights" element={<AIInsightsPage />} />
                <Route path="users" element={<UsersPage />} />
              </Route>

              {/* Storefront routes */}
              <Route path="/*" element={
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <main className="flex-1">
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/store" element={<StorePage />} />
                      <Route path="/brand/:slug" element={<StorePage />} />
                      <Route path="/flavour/:slug" element={<StorePage />} />
                      <Route path="/strength/:slug" element={<StorePage />} />
                      <Route path="/velo" element={<BrandPage />} />
                      <Route path="/velo-shift" element={<BrandPage />} />
                      <Route path="/zyn" element={<BrandPage />} />
                      <Route path="/pablo" element={<BrandPage />} />
                      <Route path="/white-fox" element={<BrandPage />} />
                      <Route path="/clew" element={<BrandPage />} />
                      <Route path="/city" element={<CityIndexPage />} />
                      <Route path="/city/:slug" element={<CityPage />} />
                      <Route path="/product/:id" element={<ProductDetailPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/blog" element={<BlogIndexPage />} />
                      <Route path="/blog/:slug" element={<BlogDetailPage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/verify-email" element={<VerifyEmailPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/orders" element={<MyOrdersPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/wholesale" element={<WholesalePage />} />
                      <Route path="/rewards" element={<RewardsPage />} />
                      <Route path="/rewards/coins" element={<MyCoinsPage />} />
                      <Route path="/faq" element={<FaqPage />} />
                      <Route path="/policies/refund" element={<RefundPolicyPage />} />
                      <Route path="/policies/shipping" element={<ShippingPolicyPage />} />
                      <Route path="/policies/terms" element={<TermsOfServicePage />} />
                      <Route path="/policies/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </main>
                  <Footer />
                  <CartToast />
                  <ViewCartBanner />
                </div>
              } />
            </Routes>
          </CartProvider>
          </CampaignProvider>
        </ProductsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
